import { Component, Input, Output, EventEmitter, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import {
  RestockTask,
  RestockAttemptState,
  restockTaskItemDetail,
  restockTaskStatusLabel
} from 'src/shared/services/restocking-orders.service';

/**
 * Renders a job's restock tasks — one card per attached order. Shared by the
 * upcoming job page (editable: note + unattach) and the past-job pages
 * (read-only results). Each card shows either the expected math (pending) or the
 * per-item outcome (a completed attempt). See card gCUZuCjf.
 *
 * All display strings are precomputed into view models in ngOnChanges so the
 * template never calls a function (per the codebase performance rules).
 */

interface TaskItemVM {
  name: string;
  icon: string;
  stockText: string;     // Same complete detail sentence used by the PDF.
  statusLabel: string;   // "Pending" / "Restocked" / "Not restocked"
  statusClass: string;   // .restock-status-chip modifier
  strikethrough: boolean;
}

interface TaskVM {
  orderId: number;
  state: RestockAttemptState;
  statusLabel: string;
  statusClass: string;
  note?: string;
  items: TaskItemVM[];
  notCompletedMessage?: string;   // shown for a not_restocked attempt
  showItems: boolean;
  editing: boolean;               // note-edit toggle (editable mode)
  noteDraft: string;
  saving: boolean;
}

@Component({
  selector: 'restock-tasks-display',
  templateUrl: './restock-tasks-display.component.html',
  styleUrls: ['./restock-tasks-display.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TidyUiComponentsModule,
    SharedModule
  ]
})
export class RestockTasksDisplayComponent implements OnChanges {
  @Input() tasks: RestockTask[] = [];
  /** When true, pending tasks show a note-edit affordance and an unattach control. */
  @Input() editable = false;
  @Input() saveNoteAction?: (event: { orderId: number; note: string }) => Promise<void>;

  @Output() unassignOrder = new EventEmitter<number>();

  taskVms: TaskVM[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.taskVms = (this.tasks || []).map((task) => this.toVm(task));
  }

  private toVm(task: RestockTask): TaskVM {
    const isNotRestocked = task.state === 'not_restocked';
    return {
      orderId: task.id,
      state: task.state,
      statusLabel: restockTaskStatusLabel(task.state),
      statusClass: this.statusClass(task.state),
      note: task.effectiveProNote,
      items: isNotRestocked ? [] : (task.items || []).map((item) => this.toItemVm(task.state, item)),
      notCompletedMessage: isNotRestocked
        ? 'Restocking was expected on this job — not completed.'
        : undefined,
      showItems: !isNotRestocked && (task.items || []).length > 0,
      editing: false,
      noteDraft: task.effectiveProNote || '',
      saving: false,
    };
  }

  private toItemVm(state: RestockAttemptState, item: RestockTask['items'][number]): TaskItemVM {
    const base = {
      name: item.name,
      icon: item.icon || 'assets/svg/cube-outline.svg',
      stockText: restockTaskItemDetail(state, item),
    };
    if (state === 'pending') {
      return {
        ...base,
        statusLabel: 'Pending',
        statusClass: 'ordered',
        strikethrough: false,
      };
    }
    // Completed attempt (restocked): read-only per-item outcome.
    if (item.restocked) {
      return {
        ...base,
        statusLabel: 'Restocked',
        statusClass: 'restocked',
        strikethrough: false,
      };
    }
    return {
      ...base,
      statusLabel: 'Not restocked',
      statusClass: 'cancelled',
      strikethrough: true,
    };
  }

  // Reuse the existing .restock-status-chip color modifiers (see global.scss).
  private statusClass(state: RestockAttemptState): string {
    switch (state) {
      case 'restocked':
        return 'restocked';
      case 'not_restocked':
        return 'cancelled';
      default:
        return 'ordered';
    }
  }

  startEditNote(task: TaskVM): void {
    task.noteDraft = task.note || '';
    task.editing = true;
  }

  cancelEditNote(task: TaskVM): void {
    task.editing = false;
    task.noteDraft = task.note || '';
  }

  async onSaveNote(task: TaskVM): Promise<void> {
    if (task.saving || !this.saveNoteAction) {
      return;
    }
    const note = (task.noteDraft || '').trim();
    task.saving = true;
    try {
      await this.saveNoteAction({ orderId: task.orderId, note });
      task.note = note;
      task.editing = false;
    } catch (_error) {
      // The parent owns the error toast. Keep the draft and editor open for retry.
    } finally {
      task.saving = false;
      this.cdr.markForCheck();
    }
  }

  onUnassign(task: TaskVM): void {
    this.unassignOrder.emit(task.orderId);
  }
}
